import { useClerk, useReverification } from '@clerk/react-router';
import type { EmailAddressResource, UserResource } from '@clerk/react-router/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { Button } from '~/components/buttons/Button/Button';
import FieldError from '~/components/FieldError';
import { FormActions } from '~/components/forms/FormActions/FormActions';
import { FormLayout } from '~/components/forms/FormLayout';
import { FormRow } from '~/components/forms/FormRow';
import { FormSection } from '~/components/forms/FormSection';
import { TextInput } from '~/components/forms/TextInput/TextInput';
import { useCustomForm } from '~/hooks/useCustomForm';
import { buildBrandSwitchUrl } from '~/routing/brandSwitch';
import { paths } from '~/routing/routes';
import { E164_PATTERN, normalizePhoneNumber, PHONE_FORMAT_MESSAGE } from '~/utils/phone';
import { postData } from '~/utils/service';
import { AccountDeactivationSection } from './AccountDeactivationSection';
import {
  buildExternalAccountRedirectUrl,
  cleanClerkReturnParams,
  clearConnectAttempt,
  markConnectAttempt,
  peekConnectAttempt,
  readClerkReturnFailure,
} from './externalAccountReturn';
import {
  SETTINGS_ACTION_ROW_CLASS,
  SETTINGS_CONTROLS_CLASS,
  SETTINGS_INTRO_CLASS,
  SETTINGS_SECTION_CLASS,
} from './settingsLayout';
import type { ProviderAccountName } from './useProviderAccountName';

interface Props {
  /** Render without its own white card, for a host that already supplies the panel. */
  embedded?: boolean;
  /** Replaces the note above the form for a host whose name is not tied to a credentialing profile. */
  nameNote?: ReactNode;
  user: UserResource;
  /**
   * [MCV-1834] Set for a provider: the name is the MCV profile's, shown read-only here with a link
   * to edit it, and nothing in this form writes the Clerk name. `null` is a provider whose profile
   * has not loaded yet; `undefined` is anyone else, who edits the Clerk name as before.
   */
  providerName?: ProviderAccountName | null;
  /**
   * [MCV-1834] Set where the vault routes are not mounted (the HLX host): "Edit in Profile" is then
   * a native link to the profile on the MCV origin, because an in-app link there bounces home.
   */
  profileOnMcvHost?: boolean;
}

const PROVIDER_NAME_NOTE =
  'Note: Your name and photo will be displayed publicly so be careful what you share. Your name comes from your MCV profile; edit it there.';

const DEFAULT_NAME_NOTE =
  'Note: Your name and photo will be displayed publicly so be careful what you share. Your name will be used for this app and not affect what you have entered for your credentialing profile.';

const nameSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});
const emailSchema = z.object({
  email: z.string().trim().min(1, 'Email address is required').email('Enter a valid email address'),
});
const emailCodeSchema = z.object({
  emailCode: z
    .string()
    .length(6, 'Enter the 6-digit code we emailed you')
    .regex(/^\d{6}$/, 'Enter the 6-digit code we emailed you'),
});
const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
});
const totpSchema = z.object({
  code: z
    .string()
    .length(6, 'Enter the 6-digit code from your authenticator app')
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});
const phoneSchema = z.object({
  // [MCV-1515] Same normalisation as sign-up — this field rejected ordinary US
  // formatting in exactly the same way.
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .transform(normalizePhoneNumber)
    .refine((value) => E164_PATTERN.test(value), PHONE_FORMAT_MESSAGE),
});
const phoneCodeSchema = z.object({
  code: z
    .string()
    .length(6, 'Enter the 6-digit code we texted you')
    .regex(/^\d{6}$/, 'Enter the 6-digit code we texted you'),
});
type NameValues = z.infer<typeof nameSchema>;
type EmailValues = z.infer<typeof emailSchema>;
type EmailCodeValues = z.infer<typeof emailCodeSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;
type TotpValues = z.infer<typeof totpSchema>;
type PhoneValues = z.infer<typeof phoneSchema>;
type PhoneCodeValues = z.infer<typeof phoneCodeSchema>;

const getClerkErrorMessage = (err: unknown, fallback: string): string => {
  const clerkErr = err as { errors?: { longMessage?: string }[] };
  return clerkErr?.errors?.[0]?.longMessage ?? fallback;
};

// [MCV-1787] Clerk answers a request it understood and said no to with an `errors`
// array. A call that never got an answer (a dismissed reverification prompt, a
// dropped connection) rejects with something else.
const isClerkApiError = (err: unknown): boolean => {
  const errors = (err as { errors?: unknown } | null)?.errors;
  return Array.isArray(errors) && errors.length > 0;
};

const REMOVAL_REFUSED_FALLBACK = 'it may be linked to a connected account.';

// An address of the old sign-in that Clerk refused to remove, with Clerk's reason.
interface RefusedEmail {
  id: string;
  address: string;
  reason: string;
}

// What is left to do for a change whose new address is already the primary.
interface UnfinishedEmailChange {
  primaryId: string;
  // Addresses already dealt with: removed, or refused. A retry does not touch them.
  doneIds: string[];
  refused: RefusedEmail[];
}

export const OAUTH_PROVIDERS = [
  { strategy: 'oauth_google' as const, label: 'Google' },
  { strategy: 'oauth_microsoft' as const, label: 'Microsoft' },
  { strategy: 'oauth_apple' as const, label: 'Apple' },
];

// [MCV-1839] What Clerk's server calls the failures it can send back on the URL.
const CLERK_RETURN_ERROR_MESSAGES: Record<string, string> = {
  oauth_access_denied: 'You did not grant access to your account.',
};

/**
 * [MCV-1839] The message for a connect attempt that has just come back failed, or
 * null when this page load is not a return from one (or the link went through).
 *
 * A return is recognised by the marker left when the user set off, or by Clerk's
 * failure params on the URL. The marker is what keeps a long-dead failed record
 * (Clerk never discards it, see MCV-1494) from resurfacing on every visit.
 */
const getReturnedConnectError = (user: UserResource): string | null => {
  const attempt = peekConnectAttempt();
  const urlFailure = readClerkReturnFailure();
  if (!attempt && !urlFailure) return null;

  const isLinked = (provider: string) =>
    user.verifiedExternalAccounts.some((account) => account.provider === provider);
  const attempted = attempt ? attempt.replace('oauth_', '') : null;
  if (attempted && isLinked(attempted) && !urlFailure) return null;

  const failed = user.externalAccounts.find(
    (account) =>
      !isLinked(account.provider) &&
      (!attempted || account.provider === attempted) &&
      account.verification?.error
  );
  const label = OAUTH_PROVIDERS.find(
    ({ strategy }) => strategy === `oauth_${failed?.provider ?? attempted}`
  )?.label;
  const subject = label ? `Couldn't connect ${label}.` : "Couldn't connect your account.";
  const reason =
    failed?.verification?.error?.longMessage ??
    (urlFailure?.code ? CLERK_RETURN_ERROR_MESSAGES[urlFailure.code] : undefined) ??
    'Please try again.';
  return `${subject} ${reason}`;
};

export const AccountSettings = ({
  embedded = false,
  user,
  nameNote,
  providerName,
  profileOnMcvHost = false,
}: Props) => {
  const isProviderName = providerName !== undefined;
  const shownNameNote = nameNote ?? (isProviderName ? PROVIDER_NAME_NOTE : DEFAULT_NAME_NOTE);
  const avatarName = providerName
    ? [providerName.firstName, providerName.lastName].filter(Boolean).join(' ')
    : (user.fullName ?? '');
  const avatarInitial = (isProviderName ? providerName?.firstName : user.firstName)?.[0];
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoSaving, setPhotoSaving] = useState(false);

  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // [MCV-1787] The address being verified. Holding the resource Clerk returned,
  // rather than looking it up on `user.emailAddresses` later, keeps the code step
  // independent of when Clerk refreshes that list.
  const [pendingEmail, setPendingEmail] = useState<EmailAddressResource | null>(null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [refusedEmails, setRefusedEmails] = useState<RefusedEmail[]>([]);
  const [unfinishedEmailChange, setUnfinishedEmailChange] = useState<UnfinishedEmailChange | null>(
    null
  );

  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  const [totpStep, setTotpStep] = useState<'idle' | 'setup' | 'codes' | 'done'>('idle');
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpSaving, setTotpSaving] = useState(false);
  const [isDisablingTotp, setIsDisablingTotp] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const [connectError, setConnectError] = useState<string | null>(() =>
    getReturnedConnectError(user)
  );

  // [MCV-1839] Back from the provider: the marker and Clerk's params have been
  // read, so clear them and leave the address bar as the page the user left. Read
  // in the initialiser above and cleared here, so a strict-mode re-run of either
  // cannot lose the message.
  useEffect(() => {
    if (peekConnectAttempt() || readClerkReturnFailure()) {
      clearConnectAttempt();
      cleanClerkReturnParams();
    }
  }, []);

  const [phoneStep, setPhoneStep] = useState<'idle' | 'verify'>('idle');
  const [pendingPhoneId, setPendingPhoneId] = useState<string | null>(null);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSuccess, setPhoneSuccess] = useState(false);

  const { signOut } = useClerk();

  // [MCV-1787] Production Clerk has reverification on: a session that has not
  // proven itself recently is asked to again (a modal) before it may add, promote
  // or remove an email address, then the call is retried. Staging has it off, so
  // a call left bare here passes QA and fails on prod.
  const createEmailAddress = useReverification((email: string) =>
    user.createEmailAddress({ email })
  );
  const setPrimaryEmailAddress = useReverification((emailAddressId: string) =>
    user.update({ primaryEmailAddressId: emailAddressId })
  );
  const destroyEmailAddress = useReverification((address: EmailAddressResource) =>
    address.destroy()
  );

  const nameMethods = useCustomForm<NameValues>({
    resolver: zodResolver(nameSchema),
    defaultValues: { firstName: user.firstName ?? '', lastName: user.lastName ?? '' },
  });
  const emailMethods = useCustomForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });
  const emailCodeMethods = useCustomForm<EmailCodeValues>({
    resolver: zodResolver(emailCodeSchema),
    defaultValues: { emailCode: '' },
  });
  const passwordMethods = useCustomForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '' },
  });
  const totpMethods = useCustomForm<TotpValues>({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: '' },
  });
  const phoneMethods = useCustomForm<PhoneValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phoneNumber: '' },
  });
  const phoneCodeMethods = useCustomForm<PhoneCodeValues>({
    resolver: zodResolver(phoneCodeSchema),
    defaultValues: { code: '' },
  });

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError(null);
    setPhotoSaving(true);
    try {
      await user.setProfileImage({ file });
    } catch (err: unknown) {
      setPhotoError(getClerkErrorMessage(err, 'Failed to update photo.'));
    } finally {
      setPhotoSaving(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    setPhotoError(null);
    setPhotoSaving(true);
    try {
      await user.setProfileImage({ file: null });
    } catch (err: unknown) {
      setPhotoError(getClerkErrorMessage(err, 'Failed to remove photo.'));
    } finally {
      setPhotoSaving(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const onNameSubmit = async (values: NameValues) => {
    setNameError(null);
    setNameSuccess(false);
    setNameSaving(true);
    try {
      await user.update({ firstName: values.firstName, lastName: values.lastName });
      setNameSuccess(true);
    } catch (err: unknown) {
      setNameError(getClerkErrorMessage(err, 'Failed to update name.'));
    } finally {
      setNameSaving(false);
    }
  };

  const primaryEmail = user.emailAddresses.find((e) => e.id === user.primaryEmailAddressId) ?? null;

  // The tail of an email change, once the new address is the primary: remove the
  // old addresses, then sync our records. The same path serves the first attempt
  // and "Try again", so a retry picks up exactly where the last run stopped.
  // Never throws: a step that fails is reported, with a way to run this again.
  const finishEmailChange = async ({ primaryId, doneIds, refused }: UnfinishedEmailChange) => {
    const done = [...doneIds];
    const refusals = [...refused];
    // The new address is the primary now, so the code step is over either way.
    const leaveCodeStep = () => {
      setPendingEmail(null);
      emailMethods.reset();
      emailCodeMethods.reset();
    };
    // A change, not an addition: nothing else should stay on file as a way in.
    for (const old of user.emailAddresses.filter(
      (e) => e.id !== primaryId && !done.includes(e.id)
    )) {
      try {
        await destroyEmailAddress(old);
        done.push(old.id);
      } catch (err: unknown) {
        if (!isClerkApiError(err)) {
          // No answer from Clerk, so nothing says this address is allowed to stay:
          // it may well still be a way in. Do not report the change as done.
          leaveCodeStep();
          setRefusedEmails(refusals);
          setUnfinishedEmailChange({ primaryId, doneIds: done, refused: refusals });
          setEmailError(
            `Your new address is now your sign-in email, but ${old.emailAddress} is still on your account because it could not be removed. Try again, or contact support if this keeps happening.`
          );
          return;
        }
        // Clerk refuses to remove an address that is linked to a connected
        // account (Google, Microsoft). That is an answer, so say what it was.
        done.push(old.id);
        refusals.push({
          id: old.id,
          address: old.emailAddress,
          reason:
            (err as { errors: { longMessage?: string }[] }).errors[0]?.longMessage ||
            REMOVAL_REFUSED_FALLBACK,
        });
      }
    }
    leaveCodeStep();
    setRefusedEmails(refusals);
    // Our own copies of the address (billing, teammate invites, the provider
    // match) only follow once the server has read the verified primary back
    // from Clerk. The endpoint is idempotent, so running it again is safe.
    try {
      await postData('userInfoSyncEmail');
      setUnfinishedEmailChange(null);
      setEmailError(null);
      setEmailSuccess(true);
    } catch {
      setUnfinishedEmailChange({ primaryId, doneIds: done, refused: refusals });
      setEmailError(
        'Your sign-in email was changed, but we could not update it on your account records. Try again, or contact support if this keeps happening.'
      );
    }
  };

  // Make a verified address the primary, then finish the change.
  // Throws only for the Clerk step that makes it the primary.
  const completeEmailChange = async (address: EmailAddressResource) => {
    await setPrimaryEmailAddress(address.id);
    await finishEmailChange({ primaryId: address.id, doneIds: [], refused: [] });
  };

  const handleRetryEmailChange = async () => {
    if (!unfinishedEmailChange) return;
    setEmailSaving(true);
    try {
      await finishEmailChange(unfinishedEmailChange);
    } finally {
      setEmailSaving(false);
    }
  };

  const onEmailSubmit = async (values: EmailValues) => {
    setEmailError(null);
    setEmailSuccess(false);
    setRefusedEmails([]);
    setUnfinishedEmailChange(null);
    const existing = user.emailAddresses.find(
      (e) => e.emailAddress.toLowerCase() === values.email.toLowerCase()
    );
    if (existing && existing.id === user.primaryEmailAddressId) {
      setEmailError('That is already your sign-in email.');
      return;
    }
    setEmailSaving(true);
    try {
      // A change that stopped after the code was accepted (a dismissed
      // reverification prompt, then a reload) leaves the address verified but not
      // primary. Clerk will not add it again, and it needs no new code: finish it.
      if (existing && existing.verification?.status === 'verified') {
        await completeEmailChange(existing);
        return;
      }
      // An attempt abandoned at the code step (a reload, or leaving the page)
      // leaves that address on the Clerk user unverified, and Clerk will not add
      // it a second time. Send a fresh code to it instead.
      const created = existing ?? (await createEmailAddress(values.email));
      await created.prepareVerification({ strategy: 'email_code' });
      setPendingEmail(created);
    } catch (err: unknown) {
      setEmailError(getClerkErrorMessage(err, 'Failed to add email address.'));
    } finally {
      setEmailSaving(false);
    }
  };

  const onEmailCodeSubmit = async (values: EmailCodeValues) => {
    if (!pendingEmail) return;
    setEmailError(null);
    setEmailSaving(true);
    try {
      // Clerk updates the resource in place, so on a retry after a later step
      // failed (a dismissed reverification prompt, a dropped connection) the
      // address is already verified and a second attempt would be refused.
      if (pendingEmail.verification?.status !== 'verified') {
        await pendingEmail.attemptVerification({ code: values.emailCode });
      }
      await completeEmailChange(pendingEmail);
    } catch (err: unknown) {
      setEmailError(getClerkErrorMessage(err, 'Verification failed.'));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleCancelEmail = async () => {
    // Hold the Verify button off until the address is gone: verifying or
    // promoting the one being destroyed would race the removal.
    setEmailSaving(true);
    try {
      if (pendingEmail) {
        try {
          await destroyEmailAddress(pendingEmail);
        } catch {
          // ignore
        }
      }
      setPendingEmail(null);
      setEmailError(null);
    } finally {
      setEmailSaving(false);
    }
  };

  const onPasswordSubmit = async (values: PasswordValues) => {
    setPasswordError(null);
    setPasswordSuccess(false);
    setPasswordSaving(true);
    try {
      await user.updatePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        signOutOfOtherSessions: false,
      });
      setPasswordSuccess(true);
      passwordMethods.reset();
    } catch (err: unknown) {
      setPasswordError(getClerkErrorMessage(err, 'Failed to update password.'));
    } finally {
      setPasswordSaving(false);
    }
  };

  // The email-code reset flow runs signed-out (Clerk's useSignIn) and the
  // /forgot-password route is only mounted under <SignedOut>; from the signed-in
  // shell the link fell through to DefaultRoleRedirect (MCV-1493). Sign out
  // first, then land on the public reset page — the same transition the
  // account-deactivation flow uses.
  const handleForgotPasswordReset = async () => {
    setPasswordError(null);
    setResettingPassword(true);
    try {
      await signOut();
    } catch {
      // Sign-out failed, so the Clerk session is still live and the reset route
      // (mounted only under <SignedOut>) would just bounce off
      // DefaultRoleRedirect — the very bug this fixes. Stay put and let the user
      // retry rather than redirecting them into that dead end.
      setPasswordError('Could not start password reset. Please try again.');
      setResettingPassword(false);
      return;
    }
    window.location.assign(paths.publicAuth.forgotPassword);
  };

  const primaryPhone = user.phoneNumbers.find((p) => p.id === user.primaryPhoneNumberId) ?? null;

  const onPhoneSubmit = async (values: PhoneValues) => {
    setPhoneError(null);
    setPhoneSuccess(false);
    setPhoneSaving(true);
    try {
      const created = await user.createPhoneNumber({ phoneNumber: values.phoneNumber });
      await created.prepareVerification();
      setPendingPhoneId(created.id);
      setPhoneStep('verify');
    } catch (err: unknown) {
      setPhoneError(getClerkErrorMessage(err, 'Failed to add phone number.'));
    } finally {
      setPhoneSaving(false);
    }
  };

  const onPhoneCodeSubmit = async (values: PhoneCodeValues) => {
    if (!pendingPhoneId) return;
    setPhoneError(null);
    setPhoneSaving(true);
    try {
      const pending = user.phoneNumbers.find((p) => p.id === pendingPhoneId);
      if (!pending) throw new Error('Pending phone number not found');
      await pending.attemptVerification({ code: values.code });
      await user.update({ primaryPhoneNumberId: pending.id });
      // Remove any previously-primary phone so there's exactly one on file
      const stale = user.phoneNumbers.filter((p) => p.id !== pending.id);
      for (const old of stale) {
        try {
          await old.destroy();
        } catch {
          // best effort — leave it if Clerk rejects (e.g. still verifying)
        }
      }
      setPendingPhoneId(null);
      setPhoneStep('idle');
      setPhoneSuccess(true);
      phoneMethods.reset();
      phoneCodeMethods.reset();
    } catch (err: unknown) {
      setPhoneError(getClerkErrorMessage(err, 'Verification failed.'));
    } finally {
      setPhoneSaving(false);
    }
  };

  const handleCancelPhone = async () => {
    if (!pendingPhoneId) {
      setPhoneStep('idle');
      return;
    }
    const pending = user.phoneNumbers.find((p) => p.id === pendingPhoneId);
    if (pending) {
      try {
        await pending.destroy();
      } catch {
        // ignore
      }
    }
    setPendingPhoneId(null);
    setPhoneStep('idle');
    setPhoneError(null);
  };

  const handleSetupTotp = async () => {
    setTotpError(null);
    setTotpSaving(true);
    try {
      const totp = await user.createTOTP();
      setTotpUri(totp.uri ?? null);
      setTotpSecret(totp.secret ?? null);
      setTotpStep('setup');
    } catch (err: unknown) {
      setTotpError(getClerkErrorMessage(err, 'Failed to start MFA setup.'));
    } finally {
      setTotpSaving(false);
    }
  };

  const handleDisableTotp = async () => {
    if (isDisablingTotp) return;
    setTotpError(null);
    setIsDisablingTotp(true);
    try {
      await user.disableTOTP();
      setTotpStep('idle');
    } catch (err: unknown) {
      setTotpError(getClerkErrorMessage(err, 'Failed to disable MFA.'));
    } finally {
      setIsDisablingTotp(false);
    }
  };

  const onTotpVerify = async (values: TotpValues) => {
    setTotpError(null);
    setTotpSaving(true);
    try {
      await user.verifyTOTP({ code: values.code });
      setTotpUri(null);
      setTotpSecret(null);
      try {
        const result = await user.createBackupCode();
        setBackupCodes(result.codes);
        setTotpStep('codes');
      } catch {
        setTotpStep('done');
      }
    } catch (err: unknown) {
      setTotpError(getClerkErrorMessage(err, 'Verification failed.'));
    } finally {
      setTotpSaving(false);
    }
  };

  // Only treat an external account as connected once its OAuth flow is actually
  // verified. Clerk creates an unverified ExternalAccount the moment "Connect"
  // is clicked — before the provider redirect completes — so counting every
  // record showed "Connected" even when the user cancelled sign-in (MCV-1494).
  const connectedStrategies = new Set(
    user.verifiedExternalAccounts.map((a) => `oauth_${a.provider}`)
  );

  // [MCV-1494] The record Clerk leaves behind for a cancelled or failed link.
  // Clerk does not discard it, so the provider already has an ExternalAccount
  // and `createExternalAccount` is not a clean retry: it starts a second link
  // for that provider and drops the failure Clerk recorded on the first. Clerk's
  // own account-management flow excludes providers already present in
  // `externalAccounts` from the connect list and re-verifies the existing record
  // instead — `reauthorize` is that path.
  const pendingAccounts = new Map(
    user.externalAccounts
      .filter((account) => !connectedStrategies.has(`oauth_${account.provider}`))
      .map((account) => [`oauth_${account.provider}`, account] as const)
  );

  const handleConnectAccount = async (strategy: (typeof OAUTH_PROVIDERS)[number]['strategy']) => {
    setConnectError(null);
    try {
      // [MCV-1494] Come back to Settings, NOT /sso-callback. That route renders
      // Clerk's <AuthenticateWithRedirectCallback />, which finalises a *sign-in
      // or sign-up* attempt; linking a provider to an already-signed-in user is
      // a different flow, so returning there dead-ends on a spinner/error page
      // even though the link itself succeeded — the user had to navigate back to
      // Settings to discover it had worked.
      // [MCV-1839] Come back to the exact page and tab the user is on (this is
      // the provider Settings page on MCV and the account page on HLX), whether
      // the attempt succeeds or fails, as Clerk's own UserProfile does.
      const redirectUrl = buildExternalAccountRedirectUrl();
      const pending = pendingAccounts.get(strategy);
      const ext = pending
        ? await pending.reauthorize({ redirectUrl })
        : await user.createExternalAccount({ strategy, redirectUrl });
      const redirectTarget = ext.verification?.externalVerificationRedirectURL;
      if (redirectTarget) {
        markConnectAttempt(strategy);
        window.location.href = redirectTarget.toString();
      } else {
        // Clerk gave nowhere to send the user, so nothing would happen.
        setConnectError("Couldn't start the connection. Please try again.");
      }
    } catch (err: unknown) {
      setConnectError(getClerkErrorMessage(err, 'Failed to connect account.'));
    }
  };

  return (
    // [MCV-1577] gap-8 rather than gap-6: the forms below now carry FormLayout's
    // 20px internal rhythm, and at a 24px page gap each Save button would sit
    // almost as far from its own fields as from the next section's heading —
    // the grouping MCV-1506 fixed and pins in AccountSettings.browser.spec.
    // The section rhythm has to stay clear of the form's own.
    <div
      className={
        embedded
          ? 'flex w-full flex-col gap-8 text-left'
          : 'mt-4 flex w-full flex-col gap-8 rounded-xl bg-white p-6 text-left shadow-md'
      }
    >
      <p className="max-w-3xl text-left body-text text-grey-500">{shownNameNote}</p>

      {/* Profile photo */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Profile photo</h2>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          <div className="flex items-center gap-4">
            {user.hasImage ? (
              <img
                src={user.imageUrl}
                alt={avatarName || 'Profile'}
                className="w-16 h-16 rounded-pill object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-pill bg-primary-50 flex items-center justify-center text-white text-xl font-semibold">
                {avatarInitial?.toUpperCase() || '?'}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <button
                type="button"
                disabled={photoSaving}
                onClick={() => photoInputRef.current?.click()}
                className="text-sm text-primary-50 hover:underline disabled:opacity-50 cursor-pointer disabled:cursor-default"
              >
                {photoSaving ? 'Uploading…' : 'Change photo'}
              </button>
              {user.hasImage && (
                <button
                  type="button"
                  disabled={photoSaving}
                  onClick={handlePhotoRemove}
                  className="text-sm text-danger hover:underline disabled:opacity-50 text-left cursor-pointer disabled:cursor-default"
                >
                  Remove photo
                </button>
              )}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />
              {photoError && <p className="text-sm text-danger">{photoError}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Name */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Name</h2>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          {isProviderName ? (
            <div data-cy="account-settings-provider-name" className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-grey-400">First name</p>
                  <p className="text-sm font-medium text-grey-100">
                    {providerName?.firstName || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-grey-400">Last name</p>
                  <p className="text-sm font-medium text-grey-100">
                    {providerName?.lastName || '—'}
                  </p>
                </div>
              </div>
              {profileOnMcvHost ? (
                <a
                  href={buildBrandSwitchUrl('mcv', paths.vault.profile)}
                  data-cy="account-settings-edit-profile"
                  className="w-fit text-sm text-primary-50 hover:underline"
                >
                  Edit in Profile
                </a>
              ) : (
                <Link
                  to={paths.vault.profile}
                  data-cy="account-settings-edit-profile"
                  className="w-fit text-sm text-primary-50 hover:underline"
                >
                  Edit in Profile
                </Link>
              )}
            </div>
          ) : (
            <FormProvider {...nameMethods}>
              <form onSubmit={nameMethods.handleSubmit(onNameSubmit)} noValidate>
                <FormLayout>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput name="firstName" label="First name" required />
                    </FormRow>
                    <FormRow>
                      <TextInput name="lastName" label="Last name" required />
                    </FormRow>
                  </FormSection>
                  {nameError && <FieldError message={nameError} className="mt-0" />}
                  {nameSuccess && <p className="text-sm text-success">Name updated.</p>}
                  <FormActions sticky={false}>
                    <Button
                      label={nameSaving ? 'Saving…' : 'Save name'}
                      type="submit"
                      disabled={nameSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          )}
        </div>
      </section>

      {/* Email address (the address used to sign in) */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Email address</h2>
          <p className="text-sm text-grey-400">
            This is the address you sign in with. We email a code to a new address before it
            replaces the current one.
          </p>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          {primaryEmail && (
            <p className="text-sm text-grey-300">
              Current: <span className="font-medium break-all">{primaryEmail.emailAddress}</span>
              {primaryEmail.verification?.status === 'verified' ? (
                <span className="ml-2 text-xs text-success">verified</span>
              ) : (
                <span className="ml-2 text-xs text-grey-500">unverified</span>
              )}
            </p>
          )}
          {emailError && <FieldError message={emailError} className="mt-0" />}
          {unfinishedEmailChange && (
            <div className={SETTINGS_ACTION_ROW_CLASS}>
              <Button
                label="Try again"
                type="button"
                onClick={handleRetryEmailChange}
                disabled={emailSaving}
              />
            </div>
          )}
          {emailSuccess && <p className="text-sm text-success">Email address updated.</p>}
          {refusedEmails.map((refused) => (
            <p key={refused.id} className="text-sm text-grey-400 break-words">
              We could not remove {refused.address} from your account: {refused.reason}
            </p>
          ))}
          {!pendingEmail && (
            <FormProvider {...emailMethods}>
              <form onSubmit={emailMethods.handleSubmit(onEmailSubmit)} noValidate>
                <FormLayout>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput
                        name="email"
                        label="New email address"
                        type="email"
                        autoComplete="email"
                        required
                      />
                    </FormRow>
                  </FormSection>
                  <FormActions sticky={false}>
                    <Button
                      label={emailSaving ? 'Sending code…' : 'Change email'}
                      type="submit"
                      disabled={emailSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          )}
          {pendingEmail && (
            <FormProvider {...emailCodeMethods}>
              <form onSubmit={emailCodeMethods.handleSubmit(onEmailCodeSubmit)} noValidate>
                <FormLayout>
                  <p className="text-sm text-grey-400 break-words">
                    We emailed a 6-digit code to {pendingEmail.emailAddress}. Enter it below.
                  </p>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput
                        name="emailCode"
                        label="Verification code"
                        autoComplete="one-time-code"
                        required
                      />
                    </FormRow>
                  </FormSection>
                  <FormActions sticky={false}>
                    <Button
                      label={emailSaving ? 'Verifying…' : 'Verify email'}
                      type="submit"
                      disabled={emailSaving}
                    />
                    <Button
                      label="Cancel"
                      type="button"
                      colorVariant="error"
                      onClick={handleCancelEmail}
                      disabled={emailSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          )}
        </div>
      </section>

      {/* Password */}
      {user.passwordEnabled && (
        <section className={SETTINGS_SECTION_CLASS}>
          <div className={SETTINGS_INTRO_CLASS}>
            <h2 className="text-base font-semibold text-grey-100">Change password</h2>
          </div>
          <div className={SETTINGS_CONTROLS_CLASS}>
            <FormProvider {...passwordMethods}>
              <form onSubmit={passwordMethods.handleSubmit(onPasswordSubmit)} noValidate>
                <FormLayout>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput
                        name="currentPassword"
                        label="Current password"
                        type="password"
                        autoComplete="current-password"
                        required
                      />
                    </FormRow>
                    <FormRow>
                      <TextInput
                        name="newPassword"
                        label="New password"
                        type="password"
                        autoComplete="new-password"
                        required
                      />
                    </FormRow>
                  </FormSection>
                  {/* [MCV-1478] A link-styled control, not a shared Button: it
                      navigates away rather than acting on this form, and its
                      disabled cursor treatment is pinned by
                      AccountSettings.browser.spec. */}
                  <button
                    type="button"
                    onClick={handleForgotPasswordReset}
                    disabled={resettingPassword}
                    className="w-fit text-left text-sm text-primary-50 hover:underline enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resettingPassword ? 'Redirecting…' : 'Forgot your current password?'}
                  </button>
                  {passwordError && <FieldError message={passwordError} className="mt-0" />}
                  {passwordSuccess && <p className="text-sm text-success">Password updated.</p>}
                  <FormActions sticky={false}>
                    <Button
                      label={passwordSaving ? 'Saving…' : 'Update password'}
                      type="submit"
                      disabled={passwordSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          </div>
        </section>
      )}

      {/* Phone number (used for 2FA) */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Phone number</h2>
          <p className="text-sm text-grey-400">
            We use your phone for two-factor authentication and account recovery.
          </p>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          {primaryPhone && (
            <p className="text-sm text-grey-300">
              Current: <span className="font-medium">{primaryPhone.phoneNumber}</span>
              {primaryPhone.verification?.status === 'verified' ? (
                <span className="ml-2 text-xs text-success">verified</span>
              ) : (
                <span className="ml-2 text-xs text-grey-500">unverified</span>
              )}
            </p>
          )}
          {phoneError && <FieldError message={phoneError} className="mt-0" />}
          {phoneSuccess && <p className="text-sm text-success">Phone number updated.</p>}
          {phoneStep === 'idle' && (
            <FormProvider {...phoneMethods}>
              <form onSubmit={phoneMethods.handleSubmit(onPhoneSubmit)} noValidate>
                <FormLayout>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput
                        name="phoneNumber"
                        label={primaryPhone ? 'New phone number' : 'Phone number'}
                        type="tel"
                        autoComplete="tel"
                        required
                      />
                    </FormRow>
                  </FormSection>
                  <FormActions sticky={false}>
                    <Button
                      label={
                        phoneSaving ? 'Sending code…' : primaryPhone ? 'Replace phone' : 'Add phone'
                      }
                      type="submit"
                      disabled={phoneSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          )}
          {phoneStep === 'verify' && (
            <FormProvider {...phoneCodeMethods}>
              <form onSubmit={phoneCodeMethods.handleSubmit(onPhoneCodeSubmit)} noValidate>
                <FormLayout>
                  <p className="text-sm text-grey-400">
                    We texted a 6-digit code to that number. Enter it below.
                  </p>
                  <FormSection componentsPerLine={2}>
                    <FormRow>
                      <TextInput
                        name="code"
                        label="Verification code"
                        autoComplete="one-time-code"
                        required
                      />
                    </FormRow>
                  </FormSection>
                  <FormActions sticky={false}>
                    <Button
                      label={phoneSaving ? 'Verifying…' : 'Verify phone'}
                      type="submit"
                      disabled={phoneSaving}
                    />
                    <Button
                      label="Cancel"
                      type="button"
                      colorVariant="error"
                      onClick={handleCancelPhone}
                      disabled={phoneSaving}
                    />
                  </FormActions>
                </FormLayout>
              </form>
            </FormProvider>
          )}
        </div>
      </section>

      {/* MFA / TOTP */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Two-factor authentication</h2>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          {user.totpEnabled ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-grey-400">Authenticator app is active.</p>
              {totpError && <p className="text-sm text-danger">{totpError}</p>}
              <div className={SETTINGS_ACTION_ROW_CLASS}>
                <Button
                  label={isDisablingTotp ? 'Disabling…' : 'Disable authenticator'}
                  type="button"
                  colorVariant="error"
                  onClick={handleDisableTotp}
                  disabled={isDisablingTotp}
                />
              </div>
            </div>
          ) : totpStep === 'idle' ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-grey-400">Add an authenticator app for extra security.</p>
              {totpError && <p className="text-sm text-danger">{totpError}</p>}
              <div className={SETTINGS_ACTION_ROW_CLASS}>
                <Button
                  label={totpSaving ? 'Setting up…' : 'Set up authenticator'}
                  type="button"
                  onClick={handleSetupTotp}
                  disabled={totpSaving}
                />
              </div>
            </div>
          ) : totpStep === 'setup' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-grey-400">
                Scan this code with your authenticator app (Google Authenticator, Authy, etc.):
              </p>
              {totpUri && (
                <QRCodeSVG
                  value={totpUri}
                  size={160}
                  aria-label="TOTP QR code"
                  className="rounded-control"
                />
              )}
              {totpSecret && (
                <p className="text-xs text-grey-500 font-mono break-all">
                  Manual key: {totpSecret}
                </p>
              )}
              <FormProvider {...totpMethods}>
                <form onSubmit={totpMethods.handleSubmit(onTotpVerify)} noValidate>
                  <FormLayout>
                    <FormSection componentsPerLine={2}>
                      <FormRow>
                        <TextInput
                          name="code"
                          label="6-digit code"
                          autoComplete="one-time-code"
                          required
                        />
                      </FormRow>
                    </FormSection>
                    {totpError && <FieldError message={totpError} className="mt-0" />}
                    <FormActions sticky={false}>
                      <Button
                        label={totpSaving ? 'Verifying…' : 'Verify'}
                        type="submit"
                        disabled={totpSaving}
                      />
                    </FormActions>
                  </FormLayout>
                </form>
              </FormProvider>
            </div>
          ) : totpStep === 'codes' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-success">Authenticator app enabled.</p>
              <p className="text-sm text-grey-300 font-medium">Save your backup codes</p>
              <p className="text-sm text-grey-400">
                Store these codes somewhere safe. Each can be used once if you lose access to your
                authenticator app.
              </p>
              <div className="grid grid-cols-2 gap-1">
                {backupCodes?.map((code) => (
                  <span
                    key={code}
                    className="text-xs font-mono text-grey-200 bg-white/5 px-2 py-1 rounded"
                  >
                    {code}
                  </span>
                ))}
              </div>
              <div className={SETTINGS_ACTION_ROW_CLASS}>
                <Button label="Done" type="button" onClick={() => setTotpStep('done')} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-success">Authenticator app enabled.</p>
          )}
        </div>
      </section>

      {/* Connected accounts */}
      <section className={SETTINGS_SECTION_CLASS}>
        <div className={SETTINGS_INTRO_CLASS}>
          <h2 className="text-base font-semibold text-grey-100">Connected accounts</h2>
        </div>
        <div className={SETTINGS_CONTROLS_CLASS}>
          {connectError && <p className="text-sm text-danger">{connectError}</p>}
          <div className="flex flex-col divide-y divide-grey-800 border-b border-grey-800">
            {OAUTH_PROVIDERS.map(({ strategy, label }) => {
              const isConnected = connectedStrategies.has(strategy);
              return (
                <div key={strategy} className="py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-grey-200">{label}</span>
                    {isConnected ? (
                      <span className="text-xs text-success font-medium">Connected</span>
                    ) : (
                      // [MCV-1494] Always "Connect". Clerk never discards the
                      // unverified ExternalAccount it created when the flow was
                      // abandoned, so labelling from `pending` meant the button
                      // read "Try again" permanently — across reloads and page
                      // changes — for a provider the user had simply decided not
                      // to link. The only state worth showing here is connected
                      // or not; `pending` still drives *how* we reconnect below.
                      <button
                        type="button"
                        onClick={() => handleConnectAccount(strategy)}
                        className="text-sm text-primary-50 hover:underline cursor-pointer"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <AccountDeactivationSection />
    </div>
  );
};
